import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import AvatarMenu from "./AvatarMenu";
import ScenarioCrumb from "./ScenarioCrumb";

interface AppShellHeaderProps {
  /** Chat-scoped chrome appears only when a session is open. */
  activeScenarioId?: string;
  activeScenarioTitle?: string;
  documentCount?: number;
  onToggleDocuments?: () => void;
  documentsOpen?: boolean;
  onLogout: () => void;
  getToken: () => string | undefined;
}

const AppShellHeader: React.FC<AppShellHeaderProps> = ({
  activeScenarioId,
  activeScenarioTitle,
  documentCount,
  onToggleDocuments,
  documentsOpen = false,
  onLogout,
  getToken,
}) => {
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  // The wordmark is home. Library is deliberately not rendered as a static
  // crumb: a crumb that never changes teaches nothing, and the home
  // affordance already says it.
  const inScenario = !!activeScenarioId && !!activeScenarioTitle;

  return (
    <header className="h-14 shrink-0 flex items-center justify-between pl-5 pr-4 bg-background/80 backdrop-blur-xl border-b border-outline-variant/20 z-40">
      <div className="flex items-center gap-1 min-w-0">
        <button
          type="button"
          onClick={() => navigate(`/${orgSlug}/library`)}
          title="Library"
          className="px-2 py-1.5 rounded-md font-headline text-[15px] font-bold uppercase tracking-tighter text-primary hover:bg-surface-container transition-colors whitespace-nowrap"
        >
          Maru Labs
        </button>

        {inScenario && (
          <>
            {/* An interpunct, not a slash: a slash reads as a filesystem path,
                and these are switchable places rather than directories. */}
            <span className="text-outline-variant text-[13px] px-0.5 select-none">·</span>
            {/* min-w-0 on the wrapper for the same reason as inside the crumb:
                without it this flex child refuses to shrink and the title
                overflows the bar instead of truncating. */}
            <div className="min-w-0">
              <ScenarioCrumb
                activeScenarioId={activeScenarioId}
                activeTitle={activeScenarioTitle}
                getToken={getToken}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-1">
        {inScenario && onToggleDocuments && (
          <>
            <button
              type="button"
              onClick={onToggleDocuments}
              title={documentsOpen ? "Hide documents" : "Show documents"}
              className={`flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md transition-colors ${
                documentsOpen
                  ? "text-primary bg-primary/10"
                  : "text-slate-400 hover:text-on-surface hover:bg-surface-container"
              }`}
            >
              <span className="material-symbols-outlined text-[19px]">
                {documentsOpen ? "folder_open" : "folder"}
              </span>
              {!!documentCount && (
                <span className="grid place-items-center min-w-[18px] h-[17px] px-1.5 rounded-full bg-surface-container-highest text-on-surface font-label text-[10.5px] font-bold tabular-nums">
                  {documentCount}
                </span>
              )}
            </button>
            <span className="w-px h-5 bg-outline-variant/50 mx-1.5" />
          </>
        )}
        <AvatarMenu onLogout={onLogout} />
      </div>
    </header>
  );
};

export default AppShellHeader;
